import { TierInfo } from "./types";

/**
 * Calculate annual quota based on target commission and commission rate
 */
export function calculateAnnualQuota(
  targetCommission: number,
  commissionRate: number,
): number {
  return commissionRate > 0
    ? Math.round(targetCommission / (commissionRate / 100))
    : 0;
}

/**
 * Calculate monthly ramped quotas based on annual quota and ramp settings
 */
export function calculateMonthlyRampedQuotas(
  annualQuota: number,
  rampMonths1to3: number,
  rampMonths4to6: number,
  rampMonths7to9: number,
  rampMonths10to12: number,
): number[] {
  const monthlyBaseQuota = annualQuota / 12;
  return [
    ...Array(3).fill((monthlyBaseQuota * rampMonths1to3) / 100),
    ...Array(3).fill((monthlyBaseQuota * rampMonths4to6) / 100),
    ...Array(3).fill((monthlyBaseQuota * rampMonths7to9) / 100),
    ...Array(3).fill((monthlyBaseQuota * rampMonths10to12) / 100),
  ];
}

/**
 * Get the ramp percentage for a specific month
 */
export function getRampPercentageForMonth(
  month: number,
  rampMonths1to3: number,
  rampMonths4to6: number,
  rampMonths7to9: number,
  rampMonths10to12: number,
): number {
  if (month <= 3) return rampMonths1to3;
  if (month <= 6) return rampMonths4to6;
  if (month <= 9) return rampMonths7to9;
  return rampMonths10to12;
}

/**
 * Calculate the adjusted commission rate based on ramp percentage
 */
export function calculateAdjustedCommissionRate(
  commissionRate: number,
  rampPercentage: number,
): number {
  return rampPercentage > 0
    ? (commissionRate * 100) / rampPercentage
    : commissionRate;
}

/**
 * Calculate tier thresholds based on annual quota and accelerators
 */
export function calculateTierThresholds(
  annualQuota: number,
  accelerators: { threshold: number; multiplier: number }[],
): number[] {
  return accelerators.map((acc) => (annualQuota * acc.threshold) / 100);
}

/**
 * Calculate tier portions for accelerator tiers
 */
export function calculateTierPortions(
  actualMonthSales: number,
  previousMonthsRevenue: number,
  totalRevenueSoFar: number,
  tierThresholds: number[],
  annualQuotaPercentage: number,
  firstAcceleratorThreshold: number,
): TierInfo {
  // Initialize tier variables
  let normalPortion = 0;
  const tierPortions = new Array(tierThresholds.length).fill(0);

  // Apply accelerators if total revenue exceeds first accelerator threshold percentage of annual quota
  if (
    tierThresholds.length > 0 &&
    annualQuotaPercentage > firstAcceleratorThreshold
  ) {
    // Tier 0: Normal commission rate (below first threshold)
    normalPortion = Math.max(
      0,
      previousMonthsRevenue >= tierThresholds[0]
        ? 0 // If already past first threshold, all revenue gets accelerated
        : tierThresholds[0] - previousMonthsRevenue,
    );

    // Calculate portions for each tier
    for (let i = 0; i < tierThresholds.length; i++) {
      const currentThreshold = tierThresholds[i];
      const nextThreshold =
        i < tierThresholds.length - 1 ? tierThresholds[i + 1] : Infinity;

      if (totalRevenueSoFar > currentThreshold) {
        const tierStart = Math.max(previousMonthsRevenue, currentThreshold);
        const tierEnd = Math.min(totalRevenueSoFar, nextThreshold);
        tierPortions[i] = Math.max(0, tierEnd - tierStart);
      }
    }

    // Verify all portions add up to the month's total
    const allPortions = [normalPortion, ...tierPortions];
    const totalPortions = allPortions.reduce(
      (sum, portion) => sum + portion,
      0,
    );

    if (Math.abs(totalPortions - actualMonthSales) > 0.01) {
      // Adjust any rounding errors - apply to highest tier with revenue first
      const diff = actualMonthSales - totalPortions;

      // Find highest tier with revenue
      for (let i = tierPortions.length - 1; i >= 0; i--) {
        if (tierPortions[i] > 0) {
          tierPortions[i] += diff;
          break;
        }
      }

      // If no tier has revenue, adjust normal portion
      if (totalPortions === 0) {
        normalPortion += diff;
      }
    }
  }

  return {
    normalPortion: normalPortion || 0,
    tierPortions: tierPortions,
    tierThresholds: tierThresholds,
    annualQuotaPercentage: Math.round(annualQuotaPercentage) || 0,
  };
}

/**
 * Calculate commissions for each tier
 */
export function calculateTierCommissions(
  tierInfo: TierInfo,
  adjustedCommissionRate: number,
  acceleratorMultipliers: number[],
): {
  normalCommission: number;
  tierCommissions: number[];
  totalCommission: number;
} {
  const normalCommission =
    (tierInfo.normalPortion * adjustedCommissionRate) / 100;

  // Calculate commission for each tier
  const tierCommissions = tierInfo.tierPortions.map((portion, index) => {
    const multiplier =
      index < acceleratorMultipliers.length ? acceleratorMultipliers[index] : 1; // Fallback to 1x if no multiplier defined
    return (portion * adjustedCommissionRate * multiplier) / 100;
  });

  // Calculate total commission
  const totalCommission =
    normalCommission +
    tierCommissions.reduce((sum, commission) => sum + commission, 0);

  return {
    normalCommission,
    tierCommissions,
    totalCommission,
  };
}

/**
 * Format a number as currency
 */
export function formatCurrency(value: number): string {
  return value.toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });
}

/**
 * Format a number as percentage
 */
export function formatPercentage(value: number, decimals = 1): string {
  return value.toFixed(decimals) + "%";
}
